import { useRef, useState } from "react";
import { hexToHsv, hsvToHex, normalizeHex, type Hsv } from "../theme/color";
import { useDialog } from "../ui/dialog";
import { HexInput } from "./HexInput";

/**
 * ═══ CHOISIR UNE COULEUR SANS PASSER PAR LA FENÊTRE DU SYSTÈME ═══
 *
 * ⚠️ CE QU'ELLE REMPLACE, ET POURQUOI : la fenêtre de `input type="color"` s'ouvre sur
 * TROIS CASES R, G, B (relevé par William le 2026-09-22). Une charte graphique ne donne
 * jamais « 145, 188, 143 » : elle donne « #91BC8F ». L'organisateur devait donc trouver
 * le chevron qui fait tourner la fenêtre native sur son onglet hexadécimal — un réglage
 * DU NAVIGATEUR, que rien dans la page ne décide et qui n'existe pas sur tous. D'où ce
 * sélecteur, qui appartient au panneau : le code hexadécimal y est le seul chiffre
 * montré, toujours ouvert, jamais à aller chercher.
 *
 * Le geste, lui, ne change pas : un carré où l'on promène la couleur, un rail de
 * teintes, une pipette quand le navigateur en a une (voir `EyeDropper` plus bas) — ce
 * que l'organisateur tenait déjà dans la fenêtre native, et dans tous ses autres outils.
 *
 * ⚠️ LA COULEUR S'APPLIQUE EN DIRECT, comme le faisait la pastille native : la page
 * derrière se repeint pendant qu'on promène le curseur, et c'est tout l'intérêt du
 * panneau. Le voile de la modale est donc DISCRET (voir `.cpick::backdrop`) — un
 * sélecteur qui cacherait ce qu'il colore ne servirait à rien.
 *
 * ⚠️ SES CLASSES SONT `cpick`, PAS `pick` : `.pick` est DÉJÀ le choix segmenté du
 * panneau (`Row`, `RecapFields`), et il porte `display: flex`. Sous ce nom, la boîte
 * FERMÉE se dépliait dans la ligne — `display` d'un auteur l'emporte sur le
 * `dialog:not([open]) { display: none }` du navigateur — et chaque couleur traînait
 * son sélecteur ouvert au bas du panneau. Vu à l'écran avant d'être écrit ici.
 *
 * Displayed strings are French: the organiser is a French speaker.
 */

/** Le pas des flèches du clavier dans le carré, en fractions de lui-même. */
const STEP = 0.02;

const clamp = (n: number) => Math.min(1, Math.max(0, n));

/**
 * La pipette du navigateur — Chromium l'a, Firefox et Safari non. Elle n'est pas dans
 * les types du DOM, d'où cette déclaration : c'est une capacité qu'on DEMANDE au
 * navigateur, et dont on se passe sans un mot quand il ne l'a pas.
 */
type EyeDropper = new () => { open: () => Promise<{ sRGBHex: string }> };

const eyeDropper = (): EyeDropper | undefined =>
  (globalThis as { EyeDropper?: EyeDropper }).EyeDropper;

/**
 * Deux images : la première peint la page sans son voile, la seconde attend qu'elle
 * soit à l'écran. Chromium sous Windows et Linux PHOTOGRAPHIE l'écran à l'ouverture de
 * la pipette, et lit ensuite dans cette photo : ouverte tout de suite, elle y
 * trouverait encore le voile.
 */
const nextPaint = () =>
  new Promise<void>((done) =>
    requestAnimationFrame(() => requestAnimationFrame(() => done())),
  );

export function ColorPicker({
  open,
  color,
  value,
  label,
  placeholder,
  onChange,
  onClose,
}: {
  open: boolean;
  /** La couleur que montrent le carré et le rail : ce qui est rangé, ou le repli. */
  color: string;
  /** Ce qui est rangé sous la clé, tel quel — ce que montre le champ du code. */
  value: string;
  label: string;
  placeholder: string;
  onChange: (hex: string) => void;
  onClose: () => void;
}) {
  const dialog = useDialog(open, onClose);
  const frame = useRef<HTMLDivElement>(null);
  /** Un geste vient de finir : le clic qui le suit n'est pas un clic « à côté ». */
  const gesture = useRef(false);

  const [hsv, setHsv] = useState<Hsv>(() => hexToHsv(color));
  /**
   * ⚠️ LA TEINTE EST GARDÉE ICI, PAS RELUE DE LA COULEUR. Un gris n'a pas de teinte :
   * relire `#7f7f7f` rendrait 0°, et descendre la saturation à zéro ferait sauter le
   * rail sur le rouge — l'organisateur perdrait la teinte qu'il était en train de
   * chercher. L'état n'est donc repris que quand la couleur a changé AILLEURS : le
   * champ de la ligne, un autre réglage, la couleur héritée qu'on reprend.
   */
  const [seen, setSeen] = useState(color);
  /**
   * ⚠️ LA PIPETTE EST SORTIE : LE VOILE EST LEVÉ. Elle lit L'ÉCRAN, pas la page — voile
   * compris. Un bleu prélevé sur le site revenait assombri de 15 % et tiré vers le brun
   * (`#0055ff` → `#044bdb`), et c'est cette couleur-là qui s'écrivait : le bleu visé
   * n'était jamais pris (relevé le 2026-09-28). Voir `.cpick--pipette::backdrop`.
   */
  const [sampling, setSampling] = useState(false);
  if (color !== seen) {
    setSeen(color);
    if (color !== hsvToHex(hsv)) setHsv(hexToHsv(color));
  }

  const apply = (next: Hsv) => {
    setHsv(next);
    onChange(hsvToHex(next));
  };

  /**
   * LE GESTE DANS LE CARRÉ — poser, puis promener.
   *
   * ⚠️ IL ÉCOUTE LA FENÊTRE, PAS LE CARRÉ, pour la raison déjà mesurée dans
   * `SafeAreaPicker` : `setPointerCapture` tombe au premier rendu déclenché par
   * l'écriture, et le geste s'arrêterait au premier mouvement — or chaque mouvement
   * écrit ici. Sur la fenêtre, il continue aussi quand le pointeur sort du carré, ce
   * qui compte puisque c'est souvent le BORD qu'on cherche (le blanc, le noir, le pur).
   */
  const begin = (event: React.PointerEvent) => {
    const box = frame.current?.getBoundingClientRect();
    // Un carré sans surface ne dit rien d'une position : diviser par sa largeur
    // rendrait `NaN`, et `NaN` s'écrirait dans le réglage.
    if (!box?.width || !box.height) return;
    event.preventDefault();
    gesture.current = true;

    /* La teinte ne bouge pas pendant le geste : le carré ne promène QUE la saturation
       et la valeur. Capturée ici, elle échappe à l'état périmé des écouteurs. */
    const h = hsv.h;
    const at = (point: { clientX: number; clientY: number }) =>
      apply({
        h,
        s: clamp((point.clientX - box.left) / box.width),
        v: 1 - clamp((point.clientY - box.top) / box.height),
      });

    at(event);
    const move = (e: PointerEvent) => at(e);
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  /* Le carré au clavier : sans ça, il n'y aurait aucun chemin vers une couleur qui ne
     passe par la souris — le rail et le code en ont un, le carré doit l'avoir aussi. */
  const keys = (event: React.KeyboardEvent) => {
    const dx = event.key === "ArrowRight" ? 1 : event.key === "ArrowLeft" ? -1 : 0;
    const dy = event.key === "ArrowUp" ? 1 : event.key === "ArrowDown" ? -1 : 0;
    if (!dx && !dy) return;
    event.preventDefault();
    apply({
      h: hsv.h,
      s: clamp(hsv.s + dx * STEP),
      v: clamp(hsv.v + dy * STEP),
    });
  };

  const Dropper = eyeDropper();

  return (
    <dialog
      ref={dialog.ref}
      onCancel={dialog.onCancel}
      /* ⚠️ LE CLIC QUI SUIT UN GESTE N'EST PAS UN CLIC DEHORS. Un curseur lâché
         au-delà du carré rend un `click` sur l'ancêtre commun — la boîte elle-même —
         et le sélecteur se fermait au milieu du geste, sur la couleur de passage. */
      onClick={(e) => {
        if (gesture.current) {
          gesture.current = false;
          return;
        }
        dialog.onClick(e);
      }}
      className={sampling ? "ask cpick cpick--pipette" : "ask cpick"}
      aria-label={label}
    >
      <div className="ask__b">
        <div className="cpick__h">
          <b>{label}</b>
          <button
            type="button"
            className="cpick__x"
            aria-label="Fermer"
            onClick={onClose}
          >
            ✕
          </button>
        </div>

        <div
          ref={frame}
          className="cpick__sv"
          /* Le carré de la teinte courante : le blanc à gauche, le noir en bas — les
             deux dégradés que toute fenêtre de couleur superpose depuis trente ans. */
          style={{
            backgroundImage: `linear-gradient(to top, #000, rgb(0 0 0 / 0)), linear-gradient(to right, #fff, ${hsvToHex(
              { h: hsv.h, s: 1, v: 1 },
            )})`,
          }}
          tabIndex={0}
          role="group"
          aria-label="Saturation et luminosité"
          onPointerDown={begin}
          onKeyDown={keys}
        >
          <i
            className="cpick__dot"
            style={{
              insetInlineStart: `${hsv.s * 100}%`,
              insetBlockStart: `${(1 - hsv.v) * 100}%`,
              background: color,
            }}
          />
        </div>

        {/* Le rail des teintes. Un `input type="range"` plutôt qu'une barre dessinée à
            la main : les flèches, Origine et Fin y marchent sans une ligne de code. */}
        <input
          type="range"
          className="cpick__hue"
          min={0}
          max={359}
          step={1}
          value={Math.round(hsv.h) % 360}
          aria-label="Teinte"
          onChange={(e) => apply({ ...hsv, h: Number(e.target.value) })}
        />

        <div className="cpick__f">
          <span
            className="cpick__now"
            style={{ background: color }}
            aria-hidden="true"
          />
          {/* ⚠️ LE CODE, ET RIEN QUE LUI — c'est tout l'objet de cette fenêtre. Trois
              cases R, G, B ne se collent pas depuis une charte ; un hexadécimal, si. */}
          <HexInput
            className="cpick__hex"
            label={label}
            value={value}
            placeholder={placeholder}
            onChange={onChange}
          />
          {Dropper && (
            <button
              type="button"
              className="cpick__eye"
              aria-label="Prélever une couleur à l'écran"
              title="Prélever une couleur à l'écran"
              onClick={() => {
                /* Un second clic avant l'ouverture rouvrirait une pipette, et la
                   fin de la seconde remettrait le voile sous la première. */
                if (sampling) return;
                setSampling(true);
                void nextPaint()
                  .then(() => new Dropper().open())
                  .then(({ sRGBHex }) => {
                    const hex = normalizeHex(sRGBHex);
                    if (hex) apply(hexToHsv(hex));
                  })
                  /* Échap sur la pipette : l'organisateur a renoncé, il n'y a rien
                     à lui dire. */
                  .catch(() => {})
                  .finally(() => setSampling(false));
              }}
            >
              ⌖
            </button>
          )}
        </div>
      </div>
    </dialog>
  );
}
